/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { RecordingOptions } from "client-code/cloudcore-recordings";

import {
  ActionKernelType,
  CONTENT_DETAIL_SEED_TYPE,
  RecordingOptionType
} from "../../model/types";
import { ChannelsInteractor } from "../../model/interactor/channelsInteractor";
import {
  IdType,
  NdvrPlaybackType,
  PlaybackContentType
} from "../../model/videoplayer/enums";
import type { IPlayable } from "../../model/videoplayer/interfaces";
import type {
  ContentDetailActionData,
  ContentDetailRecordingOptionGroup,
  ContentDetailSeed
} from "./contentDetail.types";
import type { TRecordingScheduled } from "../utility/recordingEvents";
import { Locale, LocaleKeys } from "../../model/locale";
import type { TContentDetailFavoriteTarget } from "../../model/interfaces/IContentDetailInteractor";
import { CONTENT_DETAIL_FAVORITE_MODE } from "../../model/interfaces/IContentDetailInteractor";

const RECORDING_VALUE_FIELD = {
  TIME_AND_CHANNEL: "timeAndChannel",
  KEEP_UNTIL: "keepUntil",
  START_RECORDING_EARLY: "startRecordingEarly",
  STOP_RECORDING_LATE: "stopRecordingLate"
} as const;

const CONTENT_FAVORITE_KERNELS: ReadonlySet<string> = new Set<string>([
  ActionKernelType.BookmarkThisShowActionKernel,
  ActionKernelType.BookmarkThisMovieActionKernel,
  ActionKernelType.BookmarkThisEpisodeActionKernel,
  ActionKernelType.BookmarkThisGameActionKernel,
  ActionKernelType.BookmarkThisMatchActionKernel,
  ActionKernelType.BookmarkThisRaceActionKernel,
  ActionKernelType.BookmarkThisEventActionKernel
]);

export function getContentDetailFavoriteTarget(
  action: ContentDetailActionData
): TContentDetailFavoriteTarget | null {
  const contentId = readContentDetailActionString(action, "contentId");
  const collectionId = readContentDetailActionString(action, "collectionId");
  if (action.kernelType === ActionKernelType.DeleteBookmarkActionKernel) {
    return contentId === undefined
      ? null
      : { mode: CONTENT_DETAIL_FAVORITE_MODE.REMOVE, contentId };
  }
  if (CONTENT_FAVORITE_KERNELS.has(action.kernelType)) {
    return contentId === undefined || collectionId === undefined
      ? null
      : { mode: CONTENT_DETAIL_FAVORITE_MODE.ADD, contentId, collectionId };
  }
  if (action.sourceAction === undefined || collectionId === undefined) {
    return null;
  }
  const kernel: Record<string, unknown> = { ...action.sourceAction.kernel };
  if (
    action.kernelType ===
      ActionKernelType.AddSeasonStreamingVideoActionKernel &&
    typeof kernel.seasonNumber === "number"
  ) {
    return {
      mode: CONTENT_DETAIL_FAVORITE_MODE.SEASON,
      unifiedCollectionId: collectionId,
      season: kernel.seasonNumber
    };
  }
  if (
    action.kernelType === ActionKernelType.AddYearStreamingVideoActionKernel &&
    typeof kernel.year === "number"
  ) {
    return {
      mode: CONTENT_DETAIL_FAVORITE_MODE.YEAR,
      unifiedCollectionId: collectionId,
      year: kernel.year
    };
  }
  return null;
}

/** Normalizes recording option dependencies without exposing service DTOs to views. */
export function buildContentDetailRecordingOptionGroups(
  options: RecordingOptions
): ContentDetailRecordingOptionGroup[] {
  const fields: Record<string, string> = {
    [RecordingOptionType.TimeAndChannel]:
      RECORDING_VALUE_FIELD.TIME_AND_CHANNEL,
    [RecordingOptionType.KeepUntil]: RECORDING_VALUE_FIELD.KEEP_UNTIL,
    [RecordingOptionType.StartRecording]:
      RECORDING_VALUE_FIELD.START_RECORDING_EARLY,
    [RecordingOptionType.StopRecording]:
      RECORDING_VALUE_FIELD.STOP_RECORDING_LATE
  };
  const keys: Record<string, string> = {
    [RecordingOptionType.TimeAndChannel]:
      LocaleKeys.CONTENT_RECORDING_TIME_CHANNEL,
    [RecordingOptionType.KeepUntil]: LocaleKeys.CONTENT_RECORDING_KEEP_UNTIL,
    [RecordingOptionType.StartRecording]: LocaleKeys.CONTENT_RECORDING_START,
    [RecordingOptionType.StopRecording]: LocaleKeys.CONTENT_RECORDING_STOP
  };
  const lists = [
    options.timeAndChannel,
    options.keepUntil,
    options.startRecordingEarly,
    options.stopRecordingLate
  ];
  return lists.flatMap((list): ContentDetailRecordingOptionGroup[] => {
    if (
      list === undefined ||
      list.visibility === false ||
      list.optionListType === undefined
    ) {
      return [];
    }
    const sourceKey = list.optionListType;
    const field = fields[sourceKey];
    const choices = (
      list.optionList === undefined ? [] : list.optionList
    ).flatMap((option) => {
      const raw: Record<string, unknown> = { ...option };
      if (option.label === undefined || raw[sourceKey] === undefined) {
        return [];
      }
      const values: Record<string, unknown> = { [field]: raw[sourceKey] };
      const disabledFields: string[] = [];
      for (const disabled of option.disables === undefined
        ? []
        : option.disables) {
        if (disabled.optionListType !== undefined) {
          disabledFields.push(fields[disabled.optionListType]);
        }
      }
      for (const forced of option.forcedSelection === undefined
        ? []
        : option.forcedSelection) {
        if (forced.optionType !== undefined && forced.item !== undefined) {
          const item: Record<string, unknown> = { ...forced.item };
          if (item[forced.optionType] !== undefined) {
            values[fields[forced.optionType]] = item[forced.optionType];
            disabledFields.push(fields[forced.optionType]);
          }
        }
      }
      return [{ label: option.label, values, disabledFields }];
    });
    return choices.length === 0
      ? []
      : [{ id: field, label: Locale.getString(keys[sourceKey]), choices }];
  });
}

const MANAGE_KERNELS: ReadonlySet<string> = new Set<string>([
  ActionKernelType.ModifyRecordingActionKernel,
  ActionKernelType.ModifyKeepUntilActionKernel,
  ActionKernelType.RecoverRecordingActionKernel,
  ActionKernelType.StopAndDeleteRecordingActionKernel,
  ActionKernelType.CancelRecordingActionKernel,
  ActionKernelType.DeleteRecordingActionKernel,
  ActionKernelType.ModifyOnePassActionKernel,
  ActionKernelType.ModifyOnePassActionKernelV2,
  ActionKernelType.CancelOnePassActionKernel,
  ActionKernelType.CancelOnePassActionKernelV2,
  ActionKernelType.CreateMultiOnePassActionKernel,
  ActionKernelType.ModifyMultiOnePassActionKernel,
  ActionKernelType.CancelMultiOnePassActionKernel,
  ActionKernelType.DeleteAllEpisodesActionKernel
]);

export function isContentDetailManageAction(
  action: ContentDetailActionData
): boolean {
  return MANAGE_KERNELS.has(action.kernelType);
}

export function readContentDetailActionString(
  action: ContentDetailActionData,
  field: string
): string | undefined {
  if (action.sourceAction === undefined) {
    return undefined;
  }
  const kernel: Record<string, unknown> = { ...action.sourceAction.kernel };
  const value: unknown = kernel[field];
  return typeof value === "string" && value !== "" ? value : undefined;
}

export function getContentDetailSeriesRecordingId(
  action: ContentDetailActionData,
  seed: ContentDetailSeed | null
): string | undefined {
  const collectionId = readContentDetailActionString(
    action,
    "unifiedCollectionId"
  );
  if (collectionId !== undefined) {
    return collectionId;
  }
  const legacyCollectionId = readContentDetailActionString(
    action,
    "collectionId"
  );
  if (legacyCollectionId !== undefined) {
    return legacyCollectionId;
  }
  return seed !== null &&
    seed.seedType === CONTENT_DETAIL_SEED_TYPE.UNIFIED_COLLECTION_ID
    ? seed.seedId
    : undefined;
}

export function isContentDetailWatchAction(
  action: ContentDetailActionData
): boolean {
  return action.variant === "primary";
}

export function applyContentDetailScheduleSuccess(
  actions: ContentDetailActionData[],
  scheduled: TRecordingScheduled,
  seed: ContentDetailSeed | null
): ContentDetailActionData[] {
  const updated = actions.map(
    (action: ContentDetailActionData): ContentDetailActionData => {
      const isSingle: boolean =
        scheduled.offerId !== undefined &&
        action.recordingParams !== undefined &&
        action.recordingParams.offerId === scheduled.offerId;
      const isSeries: boolean =
        scheduled.unifiedCollectionId !== undefined &&
        (action.kernelType === ActionKernelType.CreateOnePassActionKernel ||
          action.kernelType === ActionKernelType.CreateOnePassActionKernelV2) &&
        getContentDetailSeriesRecordingId(action, seed) ===
          scheduled.unifiedCollectionId;
      if (!isSingle && !isSeries) {
        return action;
      }
      const kernelType = isSingle
        ? ActionKernelType.ModifyRecordingActionKernel
        : ActionKernelType.ModifyOnePassActionKernelV2;
      return {
        ...action,
        label: Locale.getString(
          LocaleKeys.CONTENT_ACTION_LABEL_MANAGE_RECORDINGS
        ),
        kernelType,
        recordingParams: undefined,
        sourceAction:
          action.sourceAction === undefined
            ? undefined
            : {
                ...action.sourceAction,
                kernel: {
                  ...action.sourceAction.kernel,
                  actionKernelType: kernelType
                }
              }
      };
    }
  );
  const manage = updated.find(isContentDetailManageAction);
  return manage === undefined
    ? updated
    : updated
        .filter((action) => !isContentDetailManageAction(action))
        .concat(manage);
}

export async function buildContentDetailPlayable(
  action: ContentDetailActionData
): Promise<IPlayable | null> {
  const kernelType: string = action.kernelType;
  const recordingId: string | undefined = readContentDetailActionString(
    action,
    "recordingId"
  );
  if (
    kernelType === ActionKernelType.WatchFromMyShowsActionKernel ||
    kernelType === ActionKernelType.ResumeFromMyShowsActionKernel
  ) {
    return recordingId === undefined
      ? null
      : {
          playbackContentType: PlaybackContentType.NDVR_RECORDED,
          ndvrPlaybackModel: {
            id: recordingId,
            ndvrType: NdvrPlaybackType.RECORDING,
            isStartOver:
              kernelType === ActionKernelType.WatchFromMyShowsActionKernel
          }
        };
  }
  if (
    kernelType === ActionKernelType.WatchLiveActionKernel ||
    kernelType === ActionKernelType.WatchFromSocuActionKernel
  ) {
    const channelNumber: string | undefined = readContentDetailActionString(
      action,
      "channelNumber"
    );
    const offerId: string | undefined = readContentDetailActionString(
      action,
      "socuOfferId"
    );
    if (
      channelNumber === undefined ||
      (kernelType === ActionKernelType.WatchFromSocuActionKernel &&
        offerId === undefined)
    ) {
      return null;
    }
    const channel = await new ChannelsInteractor().tuneToChannel(channelNumber);
    return kernelType === ActionKernelType.WatchLiveActionKernel
      ? {
          channel,
          playbackContentType: PlaybackContentType.LIVE
        }
      : {
          channel,
          offerId,
          playbackContentType: PlaybackContentType.STARTOVER_RESUME,
          vodPlaybackModel: {
            id: offerId === undefined ? "" : offerId,
            isStartOver: false
          }
        };
  }
  const isOffer: boolean =
    kernelType === ActionKernelType.WatchFromVodOfferActionKernel ||
    kernelType === ActionKernelType.WatchVodOfferPreviewActionKernel;
  const isContent: boolean =
    kernelType === ActionKernelType.WatchFromVodActionKernel ||
    kernelType === ActionKernelType.WatchVodContentPreviewActionKernel;
  const id: string | undefined = readContentDetailActionString(
    action,
    isOffer ? "offerId" : "contentId"
  );
  if ((!isOffer && !isContent) || id === undefined) {
    return null;
  }
  return {
    playbackContentType: PlaybackContentType.VOD,
    vodPlaybackModel: {
      id,
      idType: isOffer ? IdType.OFFER : IdType.CONTENT,
      isPreview:
        kernelType === ActionKernelType.WatchVodOfferPreviewActionKernel ||
        kernelType === ActionKernelType.WatchVodContentPreviewActionKernel,
      videoProviderId: readContentDetailActionString(action, "partnerId"),
      collapsedVodItemId: readContentDetailActionString(
        action,
        "collapsedVodItemId"
      ),
      contentSupplierPartnerId: readContentDetailActionString(
        action,
        "contentSupplierPartnerId"
      )
    }
  };
}
